import { onUnmounted } from 'vue'
import axios from 'axios'
import apiClient, { sessionExpiresAt } from '@/api/client'
import { useAuthStore } from '@/stores/auth.store'

const DEBOUNCE_MS = 60_000
const REFRESH_THRESHOLD_MS = 30 * 60 * 1000

/**
 * Proactive session refresh based on user activity.
 * When activity is detected and the session is within the refresh threshold
 * of expiry, silently refreshes the session cookie.
 */
export function useSessionRefresh() {
  let lastCheck = 0
  let refreshing = false

  const events = ['click', 'keydown', 'scroll', 'touchstart'] as const

  async function tryRefresh() {
    const now = Date.now()
    if (now - lastCheck < DEBOUNCE_MS) return
    lastCheck = now

    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) return

    const expiresAt = sessionExpiresAt.value
    if (expiresAt === null) return

    const remaining = expiresAt * 1000 - now
    console.debug(
      '[session-refresh] remaining:',
      Math.round(remaining / 1000) + 's',
      'threshold:',
      REFRESH_THRESHOLD_MS / 1000 + 's',
    )

    if (remaining <= 0 || remaining >= REFRESH_THRESHOLD_MS) return

    if (refreshing) return
    refreshing = true

    console.debug('[session-refresh] refreshing...')
    try {
      const baseUrl = apiClient.defaults.baseURL || ''
      const response = await axios.post(baseUrl + '/auth/refresh', null, {
        withCredentials: true,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      const expiresHeader = response.headers['x-session-expires']
      if (expiresHeader) {
        sessionExpiresAt.value = Number(expiresHeader)
      }
      console.debug(
        '[session-refresh] success, new expiry:',
        new Date((sessionExpiresAt.value ?? 0) * 1000).toLocaleTimeString(),
      )
    } catch {
      console.debug('[session-refresh] failed')
    } finally {
      refreshing = false
    }
  }

  function onActivity() {
    tryRefresh()
  }

  function startListening() {
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true })
    }
  }

  function stopListening() {
    for (const event of events) {
      window.removeEventListener(event, onActivity)
    }
  }

  onUnmounted(() => {
    stopListening()
  })

  return { startListening, stopListening }
}
